import ReactPixel from "react-facebook-pixel";

// Tracking IDs
export const GOOGLE_ANALYTICS_ID = "G-CM72CT7MG0";
export const FACEBOOK_PIXEL_ID = "1233498177729105";
export const CONTENTSQUARE_SCRIPT_URL =
  "https://t.contentsquare.net/uxa/c828ff7e874fc.js";

// Initialize Google Analytics
export const initGoogleAnalytics = () => {
  // Check if already initialized
  if (
    window.gtag ||
    document.querySelector(`script[src*="${GOOGLE_ANALYTICS_ID}"]`)
  ) {
    return;
  }

  // Create script element
  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  script.async = true;
  script.defer = true; // Defer loading to reduce render-blocking
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  script.onload = () => {
    window.gtag("js", new Date());
    window.gtag("config", GOOGLE_ANALYTICS_ID, {
      send_page_view: false, // We'll handle page views manually for better route tracking
    });
  };
};

// Initialize Facebook Pixel
export const initFacebookPixel = (options = {}) => {
  const defaultOptions = {
    autoConfig: true,
    debug: false,
  };

  ReactPixel.init(FACEBOOK_PIXEL_ID, { ...defaultOptions, ...options });
};

// Initialize ContentSquare
export const initContentSquare = () => {
  // Check if already initialized
  if (document.querySelector(`script[src*="contentsquare.net"]`)) {
    return;
  }

  // Create script element
  const script = document.createElement("script");
  script.src = CONTENTSQUARE_SCRIPT_URL;
  script.async = true;
  script.defer = true; // Defer loading to reduce render-blocking
  document.head.appendChild(script);
};

// Track page view
export const trackPageView = (path, title = null) => {
  // Google Analytics page view
  if (window.gtag) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: title || document.title,
    });
  }

  // Facebook Pixel page view
  ReactPixel.pageView();
};

// Track product view
export const trackViewItem = (product) => {
  const { slug, Title: name, price, documentId: id } = product;
  const value = price || 0;

  // Google Analytics view_item event
  if (window.gtag) {
    window.gtag("event", "view_item", {
      currency: "EUR",
      value: value,
      items: [
        {
          item_id: id || slug,
          item_name: name,
          price: value,
          currency: "EUR",
        },
      ],
    });
  }

  // Facebook Pixel ViewContent event
  ReactPixel.track("ViewContent", {
    content_ids: [slug || id],
    content_type: "product",
    content_name: name,
    value: value,
    currency: "EUR",
  });
};

// Track add to cart
export const trackAddToCart = (product, quantity = 1) => {
  const { slug, Title: name, price, documentId: id } = product;
  const value = (price || 0) * quantity;

  // Google Analytics add_to_cart event
  if (window.gtag) {
    window.gtag("event", "add_to_cart", {
      currency: "EUR",
      value: value,
      items: [
        {
          item_id: id || slug,
          item_name: name,
          price: price || 0,
          quantity: quantity,
          currency: "EUR",
        },
      ],
    });
  }

  // Facebook Pixel AddToCart event
  ReactPixel.track("AddToCart", {
    content_ids: [slug || id],
    content_name: name,
    content_type: "product",
    value: value,
    currency: "EUR",
  });
};

// Track initiate checkout
export const trackBeginCheckout = (items, totalValue) => {
  const gaItems = items.map((item) => ({
    item_id: item.documentId || item.slug || item.id,
    item_name: item.Title || item.name,
    price: item.price || 0,
    quantity: item.quantity || 1,
    currency: "EUR",
  }));

  // Google Analytics begin_checkout event
  if (window.gtag) {
    window.gtag("event", "begin_checkout", {
      currency: "EUR",
      value: totalValue,
      items: gaItems,
    });
  }

  // Facebook Pixel InitiateCheckout event
  ReactPixel.track("InitiateCheckout", {
    content_ids: items.map((item) => item.slug || item.documentId || item.id),
    content_type: "product",
    value: totalValue,
    currency: "EUR",
    num_items: items.length,
  });
};

// Track purchase
export const trackPurchase = (
  transactionId,
  items,
  totalValue,
  shipping = 0,
  tax = 0
) => {
  const gaItems = items.map((item) => ({
    item_id: item.documentId || item.slug || item.id,
    item_name: item.Title || item.name,
    price: item.price || 0,
    quantity: item.quantity || 1,
    currency: "EUR",
  }));

  // Google Analytics purchase event
  if (window.gtag) {
    window.gtag("event", "purchase", {
      transaction_id: transactionId || `order_${Date.now()}`,
      value: totalValue,
      currency: "EUR",
      shipping: shipping,
      tax: tax,
      items: gaItems,
    });
  }

  // Facebook Pixel Purchase event
  ReactPixel.track("Purchase", {
    content_ids: items.map((item) => item.documentId || item.slug || item.id),
    content_type: "product",
    value: totalValue,
    currency: "EUR",
    num_items: items.reduce((sum, item) => sum + (item.quantity || 1), 0),
  });
};

// Track custom events
export const trackEvent = (eventName, params = {}) => {
  // Google Analytics custom event
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }
};
